import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

const SocketContext = createContext();

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [consentRequests, setConsentRequests] = useState([]);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated && user) {
      // Initialize socket connection
      const newSocket = io(import.meta.env.VITE_SOCKET_URL, {
        transports: ['websocket', 'polling'],
        timeout: 20000,
      });

      // Connect to user's personal room
      newSocket.emit('join', user.id);

      // Listen for consent requests
      newSocket.on('consent_request', (data) => {
        console.log('Consent request received:', data);
        
        // Add to state
        setConsentRequests(prev => [data, ...prev]);
        
        // Show notification
        toast.error(
          `🚨 Emergency Access Request: Someone scanned your MedCard at ${data.location.address}`,
          {
            autoClose: false,
            closeOnClick: false,
            draggable: false,
          }
        );
        
        // Play notification sound if available
        try {
          const audio = new Audio('/notification.mp3');
          audio.play().catch(e => console.log('Audio play failed:', e));
        } catch (error) {
          console.log('Audio not available:', error);
        }
      });

      // Listen for consent results (for scanners)
      newSocket.on('consent_result', (data) => {
        console.log('Consent result received:', data);
        
        if (data.approved) {
          if (data.auto) {
            toast.success('✅ Access automatically approved (60s timeout)');
          } else {
            toast.success('✅ Access granted by user');
          }
        } else {
          toast.error('❌ Access denied by user');
        }
      });

      // Connection events
      newSocket.on('connect', () => {
        console.log('✅ Socket connected:', newSocket.id);
      });

      newSocket.on('disconnect', () => {
        console.log('❌ Socket disconnected');
      });

      newSocket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
      });

      setSocket(newSocket);

      return () => {
        newSocket.close();
      };
    } else {
      // Disconnect socket if not authenticated
      if (socket) {
        socket.close();
        setSocket(null);
      }
    }
  }, [isAuthenticated, user]);

  // Join scan room (for scanners)
  const joinScanRoom = (scanId) => {
    if (socket) {
      socket.emit('join_scan', scanId);
    }
  };

  // Remove consent request
  const removeConsentRequest = (requestId) => {
    setConsentRequests(prev => prev.filter(req => req.requestId !== requestId));
  };

  // Clear all consent requests
  const clearConsentRequests = () => {
    setConsentRequests([]);
  };

  const value = {
    socket,
    consentRequests,
    joinScanRoom,
    removeConsentRequest,
    clearConsentRequests,
    isConnected: socket?.connected || false,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketContext;
